// ET animation scripts (animations/scripts/*.script): which animation a
// character plays for each movetype (idle, run, prone ...) and event (jump,
// land ...) depending on conditions (weapon, movetype ...). Port of
// BG_AnimParseAnimScript and its helpers (bg_animation.c), including the
// line-oriented token rules of COM_ParseExt.
//
// Bit flag conditions (weapons, movetype ...) are sets of names instead of
// bit masks: weapon names get an index the first time they are seen (ET maps
// them to WP_* through the item list).

import { tokenize, type Token } from '../../shared/tokenize';
import type { Animation } from './animGroup';

// --- string tables (bg_animation.c) ---

export const ANIM_MT = {
  UNUSED: 0,
  IDLE: 1,
  IDLECR: 2,
  WALK: 3,
  WALKBK: 4,
  WALKCR: 5,
  WALKCRBK: 6,
  RUN: 7,
  RUNBK: 8,
  SWIM: 9,
  SWIMBK: 10,
  STRAFERIGHT: 11,
  STRAFELEFT: 12,
  TURNRIGHT: 13,
  TURNLEFT: 14,
  CLIMBUP: 15,
  CLIMBDOWN: 16,
  FALLEN: 17,
  PRONE: 18,
  PRONEBK: 19,
  IDLEPRONE: 20,
  FLAILING: 21,
} as const;

const MOVETYPES = ['** UNUSED **', 'IDLE', 'IDLECR', 'WALK', 'WALKBK', 'WALKCR', 'WALKCRBK', 'RUN', 'RUNBK', 'SWIM', 'SWIMBK', 'STRAFERIGHT', 'STRAFELEFT', 'TURNRIGHT', 'TURNLEFT', 'CLIMBUP', 'CLIMBDOWN', 'FALLEN', 'PRONE', 'PRONEBK', 'IDLEPRONE', 'FLAILING'];
export const NUM_MOVETYPES = MOVETYPES.length;

const EVENTS = [
  'PAIN', 'DEATH', 'FIREWEAPON', 'FIREWEAPON2', 'JUMP', 'JUMPBK', 'LAND', 'DROPWEAPON', 'RAISEWEAPON', 'CLIMBMOUNT', 'CLIMBDISMOUNT', 'RELOAD', 'PICKUPGRENADE', 'KICKGRENADE', 'QUERY',
  'INFORM_FRIENDLY_OF_ENEMY', 'KICK', 'REVIVE', 'FIRSTSIGHT', 'ROLL', 'FLIP', 'DIVE', 'PRONE_TO_CROUCH', 'BULLETIMPACT', 'INSPECTSOUND', 'SECONDLIFE', 'DO_ALT_WEAPON_MODE', 'UNDO_ALT_WEAPON_MODE',
  'DO_ALT_WEAPON_MODE_PRONE', 'UNDO_ALT_WEAPON_MODE_PRONE', 'FIREWEAPONPRONE', 'FIREWEAPON2PRONE', 'RAISEWEAPONPRONE', 'RELOADPRONE', 'TALK', 'NOPOWER',
];
export const ANIM_ET = { JUMP: 4, JUMPBK: 5, LAND: 6 } as const;
const ANIM_ET_FIREWEAPON = 2;
const ANIM_ET_FIREWEAPONPRONE = 30;

export const ANIM_BP = { UNUSED: 0, LEGS: 1, TORSO: 2, BOTH: 3 } as const;
const BODY_PARTS = ['** UNUSED **', 'LEGS', 'TORSO', 'BOTH'];
const STATES = ['RELAXED', 'QUERY', 'ALERT', 'COMBAT'];
export const MAX_AISTATES = STATES.length;
const PARSE_MODES = ['defines', 'animations', 'canned_animations', 'statechanges', 'events'];

export const ANIM_COND = {
  WEAPON: 0,
  ENEMY_POSITION: 1,
  ENEMY_WEAPON: 2,
  UNDERWATER: 3,
  MOUNTED: 4,
  MOVETYPE: 5,
  UNDERHAND: 6,
  LEANING: 7,
  IMPACT_POINT: 8,
  CROUCHING: 9,
  STUNNED: 10,
  FIRING: 11,
  SHORT_REACTION: 12,
  ENEMY_TEAM: 13,
  PARACHUTE: 14,
  CHARGING: 15,
  SECONDLIFE: 16,
  HEALTH_LEVEL: 17,
  FLAILING_TYPE: 18,
  GEN_BITFLAG: 19,
  AISTATE: 20,
} as const;
const CONDITIONS = ['WEAPONS', 'ENEMY_POSITION', 'ENEMY_WEAPON', 'UNDERWATER', 'MOUNTED', 'MOVETYPE', 'UNDERHAND', 'LEANING', 'IMPACT_POINT', 'CROUCHING', 'STUNNED', 'FIRING', 'SHORT_REACTION', 'ENEMY_TEAM', 'PARACHUTE', 'CHARGING', 'SECONDLIFE', 'HEALTH_LEVEL', 'FLAILING_TYPE', 'GEN_BITFLAG', 'AISTATE'];
export const NUM_CONDITIONS = CONDITIONS.length;

/** null = open ended name table (weapons). */
type CondTable = { type: 'bits'; values: string[] | null } | { type: 'value'; values: string[] | null };
const CONDITION_TABLES: CondTable[] = [
  { type: 'bits', values: null },
  { type: 'bits', values: ['** UNUSED **', 'BEHIND', 'INFRONT', 'RIGHT', 'LEFT'] },
  { type: 'bits', values: null },
  { type: 'value', values: null },
  { type: 'value', values: ['** UNUSED **', 'MG42'] },
  { type: 'bits', values: MOVETYPES },
  { type: 'value', values: null },
  { type: 'value', values: ['** UNUSED **', 'RIGHT', 'LEFT'] },
  { type: 'value', values: ['** UNUSED **', 'HEAD', 'CHEST', 'GUT', 'GROIN', 'SHOULDER_RIGHT', 'SHOULDER_LEFT', 'KNEE_RIGHT', 'KNEE_LEFT'] },
  { type: 'value', values: null },
  { type: 'value', values: null },
  { type: 'value', values: null },
  { type: 'value', values: null },
  { type: 'value', values: ['NAZI', 'ALLIES', 'MONSTER', 'SPARE1', 'SPARE2', 'SPARE3', 'SPARE4', 'NEUTRAL'] },
  { type: 'value', values: null },
  { type: 'value', values: null },
  { type: 'value', values: null },
  { type: 'value', values: ['1', '2', '3'] },
  { type: 'value', values: ['** UNUSED **', 'INAIR', 'VCRASH', 'HCRASH'] },
  { type: 'bits', values: ['ZOOMING'] },
  { type: 'value', values: ['RELAXED', 'QUERY', 'ALERT', 'COMBAT'] },
];

// --- bit sets with "all" ---

/** A set of bit indices; `all` = every bit except `set`. */
export class CondBits {
  constructor(
    public all = false,
    public set = new Set<number>(),
  ) {}

  static of(...bits: number[]): CondBits {
    return new CondBits(false, new Set(bits));
  }

  has(bit: number): boolean {
    return this.all ? !this.set.has(bit) : this.set.has(bit);
  }

  add(o: CondBits): void {
    if (this.all && o.all) this.set = new Set([...this.set].filter((b) => o.set.has(b)));
    else if (this.all) this.set = new Set([...this.set].filter((b) => !o.set.has(b)));
    else if (o.all) {
      this.set = new Set([...o.set].filter((b) => !this.set.has(b)));
      this.all = true;
    } else for (const b of o.set) this.set.add(b);
  }

  sub(o: CondBits): void {
    if (o.all && this.all) {
      this.set = new Set([...o.set].filter((b) => !this.set.has(b)));
      this.all = false;
    } else if (o.all) this.set = new Set([...this.set].filter((b) => o.set.has(b)));
    else if (this.all) for (const b of o.set) this.set.add(b);
    else for (const b of o.set) this.set.delete(b);
  }

  clone(): CondBits {
    return new CondBits(this.all, new Set(this.set));
  }

  /** Any bit in common (the BG_EvaluateConditions test for bit flags). */
  intersects(o: CondBits): boolean {
    if (this.all && o.all) return true;
    if (this.all) return [...o.set].some((b) => !this.set.has(b));
    if (o.all) return [...this.set].some((b) => !o.set.has(b));
    for (const b of this.set) if (o.set.has(b)) return true;
    return false;
  }
}

// --- parsed structures ---

export interface ScriptCondition {
  index: number;
  bits: CondBits | null;
  value: number;
}

export interface ScriptCommand {
  bodyPart: [number, number];
  animIndex: [number, number];
  animDuration: [number, number];
}

export interface ScriptItem {
  conditions: ScriptCondition[];
  commands: ScriptCommand[];
}

export interface AnimScript {
  items: ScriptItem[];
}

export interface AnimModel {
  animations: Animation[];
  /** [state][movetype] */
  scriptAnims: AnimScript[][];
  scriptEvents: AnimScript[];
  /** Index of each name of the open ended tables (weapons), per condition. */
  names: Map<string, number>[];
}

/** Bit index of a name in an open ended table (assigned on first use). */
export function nameIndex(model: Pick<AnimModel, 'names'>, cond: number, name: string): number {
  const table = model.names[cond === ANIM_COND.ENEMY_WEAPON ? ANIM_COND.WEAPON : cond];
  const key = name.toLowerCase();
  let i = table.get(key);
  if (i === undefined) {
    i = table.size + 1; // bit 0 is "none"
    table.set(key, i);
  }
  return i;
}

export function animationIndex(model: Pick<AnimModel, 'animations'>, name: string): number {
  const lname = name.toLowerCase();
  return model.animations.findIndex((a) => a.name === lname);
}

const indexOf = (table: string[], token: string) => table.findIndex((s) => s.toLowerCase() === token.toLowerCase());

/** COM_Parse / COM_ParseExt over the pre-split tokens. */
class Parser {
  private i = 0;
  /** Token put back with unget(): like C moving text_p back, it reads as if on the current line. */
  private ungot = -1;

  constructor(
    private readonly tokens: Token[],
    private readonly file: string,
  ) {}

  /** Next token; with allowLineBreaks false, '' at the end of the line (nothing consumed). */
  parse(allowLineBreaks = true): string {
    const t = this.tokens[this.i];
    if (!t || (!allowLineBreaks && t.newline && this.i !== this.ungot)) return '';
    this.i++;
    return t.text;
  }

  unget(): void {
    this.ungot = --this.i;
  }

  /** COM_RestoreParseSession after a successful parse. */
  restore(token: string): void {
    if (token) this.i--;
  }

  error(msg: string): never {
    const t = this.tokens[Math.max(0, this.i - 1)];
    throw new Error(`${this.file}: ${msg}${t ? ` (near "${t.text}")` : ''}`);
  }
}

export function parseAnimScript(text: string, animations: Animation[], file = 'animation script'): AnimModel {
  const model: AnimModel = {
    animations,
    scriptAnims: Array.from({ length: MAX_AISTATES }, () => Array.from({ length: NUM_MOVETYPES }, () => ({ items: [] }))),
    scriptEvents: EVENTS.map(() => ({ items: [] })),
    names: CONDITIONS.map(() => new Map()),
  };
  const p = new Parser(tokenize(text), file);
  const defines: Map<string, CondBits>[] = CONDITIONS.map(() => new Map());
  let parseMovetype: number = ANIM_MT.UNUSED;
  let parseEvent = -1;

  /** BG_ParseConditionBits */
  const parseBits = (cond: number): CondBits => {
    const table = CONDITION_TABLES[cond].values;
    const result = new CondBits();
    let current = '';
    let minus = false;
    let end = false;
    while (!end) {
      let token = p.parse(false);
      if (!token) {
        end = true;
        if (!current) break;
      }
      if (token === ',') end = true;
      const lower = token.toLowerCase();
      if (lower === 'none') {
        result.set.add(0);
        continue;
      }
      if (lower === 'none,') {
        result.set.add(0);
        end = true;
        continue;
      }
      if (lower === 'not') token = 'MINUS';
      const isOp = token.toLowerCase() === 'and' || token.toLowerCase() === 'minus';
      if (!end && !isOp) {
        if (token.endsWith(',')) {
          end = true;
          token = token.slice(0, -1);
        }
        current = current ? `${current} ${token}` : token;
      }
      if (isOp || end) {
        if (!current) {
          if (end) p.error('unexpected end of condition');
          if (token.toLowerCase() === 'minus') {
            minus = true;
            continue;
          }
          p.error(`unexpected '${token}'`);
        }
        let bits: CondBits;
        if (current.toLowerCase() === 'all') bits = new CondBits(true);
        else {
          const def = defines[cond].get(current.toLowerCase());
          if (def) bits = def.clone();
          else if (table) {
            const idx = indexOf(table, current);
            if (idx < 0) p.error(`unknown token '${current}'`);
            bits = CondBits.of(idx);
          } else bits = CondBits.of(nameIndex(model, cond, current));
        }
        if (minus) result.sub(bits);
        else result.add(bits);
        current = '';
        if (token.toLowerCase() === 'minus') minus = true;
      }
    }
    return result;
  };

  /** BG_ParseConditions */
  const parseConditions = (): ScriptItem => {
    const item: ScriptItem = { conditions: [], commands: [] };
    for (;;) {
      const token = p.parse(false);
      if (!token) break;
      if (token.toLowerCase() === 'default') return item;
      const index = indexOf(CONDITIONS, token.replace(/,$/, ''));
      if (index < 0) p.error(`unknown condition '${token}'`);
      const table = CONDITION_TABLES[index];
      if (table.type === 'bits') item.conditions.push({ index, bits: parseBits(index), value: 0 });
      else if (table.values) {
        const v = p.parse(false);
        if (!v) p.error('expected condition value, found end of line');
        const idx = indexOf(table.values, v.replace(/,$/, ''));
        if (idx < 0) p.error(`unknown token '${v}'`);
        item.conditions.push({ index, bits: null, value: idx });
      } else item.conditions.push({ index, bits: null, value: 1 });
    }
    if (!item.conditions.length) p.error('no conditions found');
    return item;
  };

  /** BG_ParseCommands */
  const parseCommands = (item: ScriptItem): void => {
    let partIndex = 0;
    let command: ScriptCommand | null = null;
    for (;;) {
      let token = p.parse(partIndex < 1);
      if (!token) break;
      if (token === '}') {
        p.unget();
        break;
      }
      if (partIndex === 0) {
        command = { bodyPart: [0, 0], animIndex: [0, 0], animDuration: [0, 0] };
        item.commands.push(command);
      }
      const cmd = command!;
      const part = indexOf(BODY_PARTS, token);
      cmd.bodyPart[partIndex] = Math.max(0, part);
      if (part > 0) {
        token = p.parse(false);
        if (!token) p.error('expected animation');
        const animIndex = animationIndex(model, token);
        if (animIndex < 0) p.error(`unknown animation '${token}'`);
        const anim = animations[animIndex];
        cmd.animIndex[partIndex] = animIndex;
        cmd.animDuration[partIndex] = anim.duration;
        // locomotion: the movetype can be told from the legs animation on the client
        if (parseMovetype !== ANIM_MT.UNUSED && part !== ANIM_BP.TORSO) anim.movetype |= 1 << parseMovetype;
        if (parseEvent === ANIM_ET_FIREWEAPON || parseEvent === ANIM_ET_FIREWEAPONPRONE) {
          anim.flags |= 0x2;
          anim.initialLerp = 40;
        }
        token = p.parse(false);
        if (token.toLowerCase() === 'duration') {
          const d = p.parse(false);
          if (!d) p.error('expected duration value');
          cmd.animDuration[partIndex] = Number.parseInt(d, 10) || 0;
        } else p.restore(token);
        if (part !== ANIM_BP.BOTH && partIndex++ < 1) continue;
      } else p.unget();
      // optional parameters (sounds)
      for (;;) {
        token = p.parse(false);
        if (!token) break;
        if (token.toLowerCase() === 'sound') {
          if (!p.parse(false)) p.error('expected sound');
        } else p.error(`unknown parameter '${token}'`);
      }
      partIndex = 0;
    }
  };

  let mode = 0;
  let indent = 0;
  const indexes = [-1, -1, -1];
  let script: AnimScript | null = null;
  let item: ScriptItem | null = null;
  for (;;) {
    const token = p.parse(true);
    if (!token) {
      if (indent) p.error('unexpected end of file');
      break;
    }
    const newMode = indexOf(PARSE_MODES, token);
    if (newMode >= 0) {
      if (indent) p.error(`unexpected '${token}'`);
      mode = newMode;
      parseMovetype = ANIM_MT.UNUSED;
      parseEvent = -1;
      continue;
    }
    if (mode === 0) {
      // defines
      if (token.toLowerCase() !== 'set') continue;
      const type = indexOf(CONDITIONS, p.parse(false));
      if (type < 0) p.error('expected condition type string');
      const name = p.parse(false);
      if (!name) p.error('expected condition define string');
      if (p.parse(false) !== '=') p.error("expected '='");
      defines[type].set(name.toLowerCase(), parseBits(type));
      // weapon defines are enemy_weapon defines too
      defines[ANIM_COND.ENEMY_WEAPON] = defines[ANIM_COND.WEAPON];
    } else if (mode === 1 || mode === 2) {
      // animations / canned_animations
      if (token === '{') {
        if (indent >= 3 || indexes[indent] < 0) p.error(`unexpected '${token}'`);
        indent++;
      } else if (token === '}') {
        indent--;
        if (indent < 0) p.error(`unexpected '${token}'`);
        if (indent === 1) script = null;
        indexes[indent] = -1;
      } else if (indent === 0 && indexes[0] < 0) {
        if (token.toLowerCase() !== 'state') p.error("expected 'state'");
        indexes[0] = indexOf(STATES, p.parse(false));
        if (indexes[0] < 0) p.error('unknown state');
        if (p.parse(true) !== '{') p.error("expected '{'");
        indent++;
      } else if (indent === 1 && indexes[1] < 0) {
        indexes[1] = indexOf(MOVETYPES, token);
        if (indexes[1] < 0) p.error(`unknown movetype '${token}'`);
        if (mode === 1) {
          script = model.scriptAnims[indexes[0]][indexes[1]];
          parseMovetype = indexes[1];
        } else script = { items: [] }; // canned animations are not used
        script.items = [];
      } else if (indent === 2 && indexes[2] < 0) {
        p.unget();
        item = parseConditions();
        indexes[2] = 1;
        script!.items.push(item);
      } else if (indent === 3) {
        p.unget();
        parseCommands(item!);
      } else p.error(`unexpected '${token}'`);
    } else if (mode === 4) {
      // events
      if (token === '{') {
        if (indent >= 3 || indexes[indent] < 0) p.error(`unexpected '${token}'`);
        indent++;
      } else if (token === '}') {
        indent--;
        if (indent < 0) p.error(`unexpected '${token}'`);
        if (indent === 0) script = null;
        indexes[indent] = -1;
      } else if (indent === 0 && indexes[0] < 0) {
        indexes[0] = indexOf(EVENTS, token);
        if (indexes[0] < 0) p.error(`unknown event '${token}'`);
        script = model.scriptEvents[indexes[0]];
        script.items = [];
        parseEvent = indexes[0];
      } else if (indent === 1 && indexes[1] < 0) {
        p.unget();
        item = parseConditions();
        indexes[1] = 1;
        script!.items.push(item);
      } else if (indent === 2) {
        p.unget();
        parseCommands(item!);
      } else p.error(`unexpected '${token}'`);
    }
  }
  return model;
}

// --- evaluation ---

/** The condition values of one character (globalScriptData->clientConditions[client]). */
export class Conditions {
  readonly bits: CondBits[] = CONDITIONS.map(() => new CondBits());
  readonly values: number[] = CONDITIONS.map(() => 0);

  /** BG_UpdateConditionValue( ..., checkConversion = qtrue ) */
  set(cond: number, value: number): void {
    if (CONDITION_TABLES[cond].type === 'bits') this.bits[cond] = CondBits.of(value);
    else this.values[cond] = value;
  }

  /** BG_GetConditionBitFlag */
  hasBit(cond: number, bit: number): boolean {
    return this.bits[cond].has(bit);
  }
}

/** BG_EvaluateConditions */
function evaluate(c: Conditions, item: ScriptItem): boolean {
  for (const cond of item.conditions) {
    if (cond.bits) {
      if (!c.bits[cond.index].intersects(cond.bits)) return false;
    } else if (c.values[cond.index] !== cond.value) return false;
  }
  return true;
}

/** BG_FirstValidItem */
export function firstValidItem(c: Conditions, script: AnimScript): ScriptItem | null {
  return script.items.find((it) => evaluate(c, it)) ?? null;
}

/** Script item for a movetype, searching the states from aiState up (BG_AnimScriptAnimation). */
export function movetypeItem(model: AnimModel, c: Conditions, aiState: number, movetype: number): ScriptItem | null {
  for (let state = aiState; state < MAX_AISTATES; state++) {
    const script = model.scriptAnims[state][movetype];
    if (!script.items.length) continue;
    const item = firstValidItem(c, script);
    if (item) return item;
  }
  return null;
}
